import React from 'react';
import { money } from './formatters';
import { FIXABLE_ON_CLINICS, heldBackLabel } from './runPresentation';

export function buildRecipientAdvisories({ data, outstandingUnconfirmed, t }) {
  return [
    data.heldBack?.count > 0 && {
      key: 'held-back',
      label: t('{count} not on this list', { count: data.heldBack.count }),
      amount: money(data.heldBack.totalOpen),
      body: (
        <>
          <p>{t('Held back when the ledger was read:')}</p>
          <ul className="mt-2 list-disc pl-5">
            {Object.entries(data.heldBack.reasons || {})
              .sort((a, b) => b[1] - a[1])
              .map(([reason, n]) => (
                <li key={reason}>
                  {n} - {t(heldBackLabel(reason))}
                </li>
              ))}
          </ul>
          {Object.keys(data.heldBack.reasons || {}).some((r) => FIXABLE_ON_CLINICS.includes(r)) && (
            <p className="mt-2">
              {t(
                'A customer nobody has ruled on can be confirmed here, on its own row. Reversing an explicit'
              )}{' '}
              <em>{t('not a practice')}</em> {t('is done on the')}{' '}
              <a href="/clinics" className="underline font-medium">
                {t('Clinics')}
              </a>{' '}
              {t(
                "page, which is where the rest of that decision's consequences are visible. Until one or the other happens they receive nothing, which is the intended behaviour - a statement asks someone for money, so the account has to have been looked at by a person first."
              )}
            </p>
          )}
        </>
      ),
    },
    outstandingUnconfirmed.count > 0 && {
      key: 'unconfirmed',
      label: t('{count} never confirmed as a practice', { count: outstandingUnconfirmed.count }),
      amount: money(outstandingUnconfirmed.totalOpen),
      body: (
        <p>
          {t(
            'Nobody has ruled on them either way. They are included here and would be sent a statement. Anything that is not a practice - a bank row, a transfer, a supplier - should be marked'
          )}{' '}
          <em>{t('Hidden')}</em> {t('on the')}{' '}
          <a href="/clinics" className="underline font-medium">
            {t('Clinics')}
          </a>{' '}
          {t(
            'page before sending is ever switched on. The ones that ARE practices can be confirmed on their own row here - filter to'
          )}{' '}
          <em>{t('Not yet confirmed as a practice')}</em> {t('to work down them.')}
        </p>
      ),
    },
    data.unusableEmails?.count > 0 && {
      key: 'unusable-emails',
      label: t(
        data.unusableEmails.count === 1
          ? '{count} address the mailbox will reject'
          : '{count} addresses the mailbox will reject',
        { count: data.unusableEmails.count }
      ),
      amount: money(data.unusableEmails.totalOpen),
      body: (
        <>
          <p>
            {t(
              'They would receive nothing. Usually it is two addresses in one QuickBooks field; the fix is in QuickBooks.'
            )}
          </p>
          {data.unusableEmails.customers?.length > 0 && (
            <>
              <ul className="mt-2 list-disc pl-5">
                {data.unusableEmails.customers.map((c, index) => (
                  <li
                    key={`${c.qbCustomerId || c.customerId || c.name}-${index}`}
                    className="break-all"
                  >
                    {c.name} - {c.email || t('no address')}
                  </li>
                ))}
              </ul>
              {data.unusableEmails.count > data.unusableEmails.customers.length && (
                <p className="mt-1">
                  {t('{shown} of {count} are named here.', {
                    shown: data.unusableEmails.customers.length,
                    count: data.unusableEmails.count,
                  })}
                </p>
              )}
            </>
          )}
        </>
      ),
    },
    data.notReconciling?.count > 0 && {
      key: 'not-reconciling',
      label: t(
        data.notReconciling.count === 1
          ? '{count} disagrees with QuickBooks'
          : '{count} disagree with QuickBooks',
        { count: data.notReconciling.count }
      ),
      body: (
        <>
          <p>
            {t('Those totals should not be sent to a customer until the difference is explained.')}
          </p>
          {/* Named, because the count is computed over the full set while the row badge only appears
              on rows the table actually shows - the banner could otherwise report a dozen with none
              findable. */}
          {data.notReconciling.customers?.length > 0 && (
            <>
              <ul className="mt-2 list-disc pl-5">
                {data.notReconciling.customers.map((c, index) => (
                  <li key={`${c.qbCustomerId || c.customerId || c.name}-${index}`}>
                    {c.name} -{' '}
                    {t('we show {ours}, QuickBooks says {theirs}', {
                      ours: money(c.totalOpen),
                      theirs: money(c.qbBalance),
                    })}
                  </li>
                ))}
              </ul>
              {/* Capped at 15 server-side. A count of forty above a list of fifteen, with nothing
                  saying so, is a list that reads as complete. */}
              {data.notReconciling.count > data.notReconciling.customers.length && (
                <p className="mt-1">
                  {t('{shown} of {count} are named here.', {
                    shown: data.notReconciling.customers.length,
                    count: data.notReconciling.count,
                  })}
                </p>
              )}
            </>
          )}
        </>
      ),
    },
    data.linesAgedFromTxnDate?.count > 0 &&
      data.linesAgedFromTxnDate.count === data.linesAgedFromTxnDate.ofTotalLines && {
        key: 'aged-from-txn-date',
        label: t('Ageing may be wrong'),
        body: (
          <p>
            {t(
              'Every invoice was aged from its transaction date because none carried a due date. That is usually a broken read, not real data - the whole ledger will look one bucket more overdue than it is.'
            )}
          </p>
        ),
      },
  ].filter(Boolean);
}
